import { getArrayValue } from "@commutifi-fe/utils";
import isNil from "lodash/isNil";
import React from "react";

export const useSelected = ({
  initialSelected,
  multiSelect = true,
  selected: controlledSelected,
}: {
  initialSelected?: string[];
  multiSelect?: boolean;
  selected?: string | string[] | null;
} = {}) => {
  const [selected, setSelected] = React.useState<Set<string>>(
    initialSelected ? new Set(getArrayValue(initialSelected)) : new Set()
  );
  const currentValue = !isNil(controlledSelected)
    ? new Set(getArrayValue(controlledSelected))
    : selected;
  const handleSelect = (id: string) => {
    const previousValues = multiSelect ? [...currentValue] : [];
    const newSelected = new Set(previousValues);

    // Force at least one value selected if we are in single select mode
    if (currentValue.has(id) && multiSelect) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }

    setSelected(newSelected);
    return newSelected;
  };

  const setValue = (id: string, isSelected: boolean) => {
    const previousValues = multiSelect ? [...currentValue] : [];
    const newSelected = new Set(previousValues);

    if (isSelected) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    return newSelected;
  };

  return { selected: currentValue, toggle: handleSelect, setValue };
};

export const useSelectedMap = <K = string, V = K>({
  initialSelected,
  multiSelect = true,
  selected: controlledSelected,
  onChange,
}: {
  initialSelected?: [K, V] | [K, V][];
  multiSelect?: boolean;
  selected?: [K, V] | [K, V][] | null;
  onChange?: (selected: V[]) => void;
} = {}) => {
  const [selected, setSelected] = React.useState<Map<K, V>>(
    initialSelected ? new Map(getArrayValue(initialSelected)) : new Map()
  );
  const currentValue = !isNil(controlledSelected)
    ? new Map(getArrayValue(controlledSelected))
    : selected;
  const handleSelect = (key: K, value: V) => {
    const previousValues = multiSelect ? [...currentValue] : [];
    const newSelected = new Map(previousValues);

    // Force at least one value selected if we are in single select mode
    if (currentValue.has(key) && multiSelect) {
      newSelected.delete(key);
    } else {
      newSelected.set(key, value);
    }

    setSelected(newSelected);
    onChange?.([...newSelected.values()]);
    return newSelected;
  };

  const addValue = (key: K, value?: V) => {
    if (typeof key !== typeof value && !value) {
      return currentValue;
    }
    const previousValues = multiSelect ? [...currentValue] : [];
    const newSelected = new Map(previousValues);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    newSelected.set(
      key,
      typeof key === typeof value ? value || (key as unknown as V) : value!
    );
    setSelected(newSelected);
    onChange?.([...newSelected.values()]);
    return newSelected;
  };

  const removeValue = (key: K) => {
    if (!currentValue.has(key)) {
      return currentValue;
    }
    const previousValues = multiSelect ? [...currentValue] : [];
    const newSelected = new Map(previousValues);

    newSelected.delete(key);
    setSelected(newSelected);
    onChange?.([...newSelected.values()]);
    return newSelected;
  };

  return {
    selected: currentValue,
    toggle: handleSelect,
    addValue,
    removeValue,
  };
};
